/**
 * ZON-275 — turns the flat school-news feed into what the Notifications page
 * shows: day sections, with each week's tournament results rolled into one
 * card so they do not flood the list. Pure; no React.
 */
import type { GolfFitAssessment } from "@/services/schoolFits";
import type {
  FitItem,
  FitSchool,
  NewsItem,
  NewsResult,
  NotificationItem,
} from "@/services/schoolNews";

export type PlaceTone = "win" | "top" | "other" | "none";

/** "Won", "2nd", "11th" ... or null when there is no stated placement. */
export function placeLabel(result: NewsResult | null | undefined): string | null {
  if (!result) return null;
  // teamWon drives the win badge; the two fields can disagree.
  if (result.teamWon) return "Won";
  const place = result.teamPlace;
  if (place === null || !Number.isInteger(place) || place < 1) return null;
  const lastTwo = place % 100;
  const suffix =
    lastTwo >= 11 && lastTwo <= 13 ? "th" : ({ 1: "st", 2: "nd", 3: "rd" }[place % 10] ?? "th");
  return `${place}${suffix}`;
}

export function placeTone(result: NewsResult | null | undefined): PlaceTone {
  if (!result) return "none";
  if (result.teamWon) return "win";
  const place = result.teamPlace;
  if (place === null || !Number.isInteger(place) || place < 1) return "none";
  return place <= 3 ? "top" : "other";
}

const TYPE_LABELS: Record<string, string> = {
  schedule: "Schedule",
  "major-award": "Award",
  postseason: "Postseason",
  "coach-change": "Coach",
  "coach-contract": "Coach",
  "player-signing": "Signing",
  "program-change": "Program",
  "national-title": "National title",
  "major-result": "Result",
};

export function typeLabel(type: string): string {
  return TYPE_LABELS[type] ?? "News";
}

/** A tournament finish that goes in the weekly roll-up. */
export function isResult(item: NewsItem): boolean {
  return item.kind === "digest" && item.type === "major-result";
}

/** A team tournament win: an alert, shown on its own with the lime badge. */
export function isWin(item: NewsItem): boolean {
  return item.kind === "alert" && item.type === "major-result";
}

export function sortNewestFirst<T extends { publishedAt: string }>(items: T[]): T[] {
  return [...items].sort((a, b) =>
    a.publishedAt < b.publishedAt ? 1 : a.publishedAt > b.publishedAt ? -1 : 0
  );
}

export type SectionId = "today" | "week" | "earlier";
const SECTION_LABELS: Record<SectionId, string> = {
  today: "Today",
  week: "This week",
  earlier: "Earlier",
};
const DAY_MS = 24 * 60 * 60 * 1000;

/** Today = same UTC day as now; This week = the last 7 days; else Earlier. */
export function sectionOf(publishedAt: string, now: Date): SectionId {
  const at = new Date(publishedAt).getTime();
  const startOfToday = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  if (at >= startOfToday) return "today";
  if (at >= now.getTime() - 7 * DAY_MS) return "week";
  return "earlier";
}

export type FeedEntry =
  | { kind: "alert"; item: NewsItem }
  | { kind: "news"; item: NewsItem }
  | { kind: "results"; key: string; items: NewsItem[] }
  | { kind: "fits"; item: FitItem };

export interface FeedSection {
  id: SectionId;
  label: string;
  entries: FeedEntry[];
}

/** Monday-based ISO week key ("2026-W40"), in UTC. */
function weekKey(publishedAt: string): string {
  const d = new Date(publishedAt);
  const t = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
  const day = t.getUTCDay() || 7;
  t.setUTCDate(t.getUTCDate() + 4 - day);
  const yearStart = Date.UTC(t.getUTCFullYear(), 0, 1);
  const week = Math.ceil(((t.getTime() - yearStart) / DAY_MS + 1) / 7);
  return `${t.getUTCFullYear()}-W${String(week).padStart(2, "0")}`;
}

/**
 * Day sections, newest first. Inside a section, digest results are rolled
 * into one entry per week, placed where that week's newest result falls.
 * The whole list is re-sorted first: fit items arrive only with page one, so
 * after "Load more" the arrival order is not date order.
 */
export function buildSections(items: NotificationItem[], now: Date): FeedSection[] {
  const sections: FeedSection[] = [];
  const rollups = new Map<string, NewsItem[]>();

  for (const item of sortNewestFirst(items)) {
    const id = sectionOf(item.publishedAt, now);
    let section = sections.find(s => s.id === id);
    if (!section) {
      section = { id, label: SECTION_LABELS[id], entries: [] };
      sections.push(section);
    }

    if (item.feed === "fits") {
      section.entries.push({ kind: "fits", item });
    } else if (isResult(item)) {
      const key = `${id}:${weekKey(item.publishedAt)}`;
      const existing = rollups.get(key);
      if (existing) {
        existing.push(item);
      } else {
        const group = [item];
        rollups.set(key, group);
        section.entries.push({ kind: "results", key, items: group });
      }
    } else if (item.kind === "alert") {
      section.entries.push({ kind: "alert", item });
    } else {
      section.entries.push({ kind: "news", item });
    }
  }

  const order: SectionId[] = ["today", "week", "earlier"];
  return sections.sort((a, b) => order.indexOf(a.id) - order.indexOf(b.id));
}

/** The Ask Coach prompt behind "Draft email to coach". */
export function draftPrompt(item: NewsItem): { autoPrompt: string; threadTitle: string } {
  const school = item.schoolName;
  const what = item.summary || item.title;
  return {
    threadTitle: school ? `Email to ${school}` : "Email to the coach",
    autoPrompt:
      `Help me draft a short email to the golf coach at ${school ?? "this school"}. ` +
      `Their program just had this news: "${what}" (${item.url}). ` +
      `Use my profile and what you know about this program. Mention the news naturally, ` +
      `say why I am interested in the program, and keep it under 150 words. ` +
      `Save it as an outreach draft so I can review it before sending.`,
  };
}

/**
 * What the school panel on the Notifications page needs to open for any
 * school in the list. `saved` is whether the school is already on My Schools:
 * a new fit is not (the panel offers Add); a school in the news is (the panel
 * links to My Schools and shows its recent news).
 */
export interface PreviewSchool {
  teamId: string;
  schoolName: string | null;
  division?: string | null;
  city?: string | null;
  state?: string | null;
  gender?: string | null;
  logoUrl?: string | null;
  fit?: GolfFitAssessment | null;
  reason?: string | null;
  saved: boolean;
  pipelineEntryId?: string | null;
}

/** A new fit: not saved yet; tier, score and reason come from the recommendation. */
export function previewFromFit(school: FitSchool): PreviewSchool {
  const hasFit = school.fitTier !== null || school.fitScore !== null;
  return {
    teamId: school.teamId,
    schoolName: school.schoolName,
    division: school.division,
    city: school.city,
    state: school.state,
    gender: school.gender,
    logoUrl: school.logoUrl,
    fit: hasFit
      ? {
          status: "ready",
          score: school.fitScore,
          tier: school.fitTier,
          summary: school.reason,
          explanation: null,
          assessedAt: null,
        }
      : null,
    reason: school.reason,
    saved: false,
  };
}

/** A school in the news is already on My Schools; its fit is filled in from there. */
export function previewFromNews(item: NewsItem): PreviewSchool {
  return {
    teamId: item.teamId,
    schoolName: item.schoolName,
    logoUrl: item.logoUrl,
    saved: true,
    pipelineEntryId: item.pipelineEntryId,
  };
}
